import { ChangeEvent, useEffect, useMemo } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  Badge,
  Button,
  Divider,
  Checkbox,
  Input as ChakraInput,
} from '@chakra-ui/react';
import { RiPieChartLine, RiErrorWarningLine } from 'react-icons/ri';
import { v4 as uuid } from 'uuid';

import {
  IExpenseRateio,
  IRateioAllocation,
  RateioMode,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';

// Item simplificado para o rateio por itens (já com valor calculado pelo pai)
export interface IRateioItem {
  id: string;
  label: string;
  value: number;
}

interface IExpenseRateioProps {
  rateio: IExpenseRateio;
  onChange: (rateio: IExpenseRateio) => void;
  total: number;
  items: IRateioItem[];
  // Centros de custo sugeridos a partir dos participantes
  participantCostCenters?: {
    cost_center: string;
    sector?: string;
    section?: string;
  }[];
}

// ----- Helpers de cálculo (exportados para reuso em PDF/Visualização) -----

export function computeAllocationValue(
  allocation: IRateioAllocation,
  mode: RateioMode,
  total: number,
  items: IRateioItem[],
): number {
  if (mode === 'percentual') {
    return Number((((allocation.percentage || 0) / 100) * total).toFixed(2));
  }
  if (mode === 'valores') {
    return Number((allocation.value || 0).toFixed(2));
  }
  // mode === 'itens'
  return Number(
    (allocation.item_ids || [])
      .reduce((sum, id) => sum + (items.find(i => i.id === id)?.value || 0), 0)
      .toFixed(2),
  );
}

export function computeRateioDistributed(
  rateio: IExpenseRateio,
  total: number,
  items: IRateioItem[],
): number {
  if (!rateio?.allocations?.length) return 0;
  return Number(
    rateio.allocations
      .reduce(
        (sum, a) => sum + computeAllocationValue(a, rateio.mode, total, items),
        0,
      )
      .toFixed(2),
  );
}

function createEmptyAllocation(): IRateioAllocation {
  return {
    id: uuid(),
    cost_center: '',
    sector: '',
    section: '',
    description: '',
    percentage: undefined,
    value: undefined,
    item_ids: [],
  };
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const labelColor = '#475569';
const fieldBorder = '#cbd5e1';

const MODES: { value: RateioMode; label: string }[] = [
  { value: 'percentual', label: 'Percentual (%)' },
  { value: 'valores', label: 'Valores (R$)' },
  { value: 'itens', label: 'Por Itens' },
];

export function ExpenseRateio({
  rateio,
  onChange,
  total,
  items,
  participantCostCenters = [],
}: IExpenseRateioProps) {
  const { mode, allocations } = rateio;

  const distributed = computeRateioDistributed(rateio, total, items);
  const remaining = Number((total - distributed).toFixed(2));
  const exceeds = distributed > total + 0.001;

  // Centros de custo distintos provenientes dos participantes.
  // Chave estável para evitar recomputo/loop quando o array é recriado.
  const ccKey = participantCostCenters
    .map(c => `${c.cost_center || ''}|${c.sector || ''}|${c.section || ''}`)
    .join(',');

  const distinctCostCenters = useMemo(() => {
    const seen = new Set<string>();
    const result: { cost_center: string; sector?: string; section?: string }[] =
      [];
    participantCostCenters.forEach(cc => {
      const ccTrim = (cc.cost_center || '').trim();
      // Agrupa pela combinação centro de custo + setor + seção: mesmo centro
      // de custo com setor/seção diferentes gera rateios separados.
      const key = `${ccTrim}|${(cc.sector || '').trim()}|${(
        cc.section || ''
      ).trim()}`;
      if (ccTrim && !seen.has(key)) {
        seen.add(key);
        result.push({
          cost_center: ccTrim,
          sector: cc.sector,
          section: cc.section,
        });
      }
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccKey]);

  // Mantém as alocações do rateio sincronizadas com os centros de custo
  // dos participantes: cria as que faltam, remove as que não existem mais e
  // atualiza setor/seção — preservando os valores (%/R$/itens) já informados.
  useEffect(() => {
    const next: IRateioAllocation[] = distinctCostCenters.map(cc => {
      const existing = allocations.find(
        a =>
          a.cost_center === cc.cost_center &&
          (a.sector || '') === (cc.sector || '') &&
          (a.section || '') === (cc.section || ''),
      );
      if (existing) {
        return { ...existing, sector: cc.sector, section: cc.section };
      }
      return {
        ...createEmptyAllocation(),
        cost_center: cc.cost_center,
        sector: cc.sector,
        section: cc.section,
      };
    });

    const changed =
      next.length !== allocations.length ||
      next.some((a, i) => {
        const cur = allocations[i];
        return (
          !cur ||
          cur.id !== a.id ||
          cur.cost_center !== a.cost_center ||
          cur.sector !== a.sector ||
          cur.section !== a.section
        );
      });

    if (changed) {
      onChange({ ...rateio, allocations: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctCostCenters, allocations]);

  const setMode = (newMode: RateioMode) => {
    onChange({ ...rateio, mode: newMode });
  };

  const updateAllocation = (id: string, patch: Partial<IRateioAllocation>) => {
    onChange({
      ...rateio,
      allocations: allocations.map(a => (a.id === id ? { ...a, ...patch } : a)),
    });
  };

  // Handler compacto para os campos numéricos (%/R$)
  const onAllocNumber =
    (id: string, field: 'percentage' | 'value') =>
    (e: ChangeEvent<HTMLInputElement>) =>
      updateAllocation(id, {
        [field]: e.target.value === '' ? undefined : Number(e.target.value),
      });

  // Em 'itens', um item só pode pertencer a uma alocação
  const toggleItem = (allocationId: string, itemId: string) => {
    onChange({
      ...rateio,
      allocations: allocations.map(a => {
        if (a.id === allocationId) {
          const has = (a.item_ids || []).includes(itemId);
          return {
            ...a,
            item_ids: has
              ? (a.item_ids || []).filter(id => id !== itemId)
              : [...(a.item_ids || []), itemId],
          };
        }
        // Remover o item de qualquer outra alocação
        return {
          ...a,
          item_ids: (a.item_ids || []).filter(id => id !== itemId),
        };
      }),
    });
  };

  const onToggleItem = (allocationId: string, itemId: string) => () =>
    toggleItem(allocationId, itemId);

  const itemOwner = (itemId: string) =>
    allocations.find(a => (a.item_ids || []).includes(itemId));

  const fillRemaining = (id: string) => {
    if (mode === 'percentual') {
      const others = allocations
        .filter(a => a.id !== id)
        .reduce((s, a) => s + (a.percentage || 0), 0);
      updateAllocation(id, {
        percentage: Number(Math.max(0, 100 - others).toFixed(2)),
      });
    } else if (mode === 'valores') {
      const others = allocations
        .filter(a => a.id !== id)
        .reduce((s, a) => s + (a.value || 0), 0);
      updateAllocation(id, {
        value: Number(Math.max(0, total - others).toFixed(2)),
      });
    }
  };

  return (
    <Box>
      <HStack spacing={2} mb={4}>
        <Icon as={RiPieChartLine} fontSize="20" color="#7c3aed" />
        <Text fontWeight="bold" fontSize="lg" color="#6d28d9">
          Rateio por Centro de Custo
        </Text>
      </HStack>

      <Text fontSize="sm" color={labelColor} mb={3}>
        Os centros de custo abaixo vêm dos participantes. Distribua o total das
        despesas entre eles — a soma não pode ultrapassar o total das despesas
        adicionadas.
      </Text>

      {/* Seletor de modo */}
      <HStack spacing={2} mb={4} flexWrap="wrap">
        {MODES.map(m => (
          <Button
            key={m.value}
            size="sm"
            variant={mode === m.value ? 'solid' : 'outline'}
            colorScheme="purple"
            onClick={() => setMode(m.value)}
          >
            {m.label}
          </Button>
        ))}
      </HStack>

      <VStack spacing={3} align="stretch">
        {allocations.length === 0 ? (
          <Box
            p={6}
            bg="white"
            borderRadius="md"
            borderWidth={2}
            borderStyle="dashed"
            borderColor="#d8b4fe"
            textAlign="center"
          >
            <Text color="#94a3b8" fontSize="sm">
              Defina o centro de custo dos participantes para configurar o
              rateio.
            </Text>
          </Box>
        ) : (
          allocations.map((allocation, index) => {
            const value = computeAllocationValue(
              allocation,
              mode,
              total,
              items,
            );
            const pctOfTotal =
              total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            return (
              <Box
                key={allocation.id}
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth={1}
                borderColor="#e9d5ff"
                borderLeftWidth="4px"
                borderLeftColor="#7c3aed"
                boxShadow="sm"
              >
                <HStack justify="space-between" mb={3} align="flex-start">
                  <Box>
                    <Badge colorScheme="purple" borderRadius="md" px={2} mb={1}>
                      Centro de Custo {index + 1}
                    </Badge>
                    <Text fontSize="md" fontWeight="bold" color="#1a202c">
                      {allocation.cost_center || '—'}
                    </Text>
                    {(allocation.sector || allocation.section) && (
                      <Text fontSize="xs" color={labelColor}>
                        {[allocation.sector, allocation.section]
                          .filter(Boolean)
                          .join(' / ')}
                      </Text>
                    )}
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="sm" fontWeight="bold" color="#6d28d9">
                      {formatCurrency(value)}
                    </Text>
                    <Text fontSize="xs" color={labelColor}>
                      ({pctOfTotal}%)
                    </Text>
                  </Box>
                </HStack>

                {mode === 'percentual' && (
                  <HStack spacing={2} align="flex-end">
                    <Box w="160px">
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={labelColor}
                        mb={1}
                      >
                        Percentual (%)
                      </Text>
                      <ChakraInput
                        size="sm"
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        placeholder="Ex: 50"
                        value={allocation.percentage ?? ''}
                        onChange={onAllocNumber(allocation.id, 'percentage')}
                        color="#1a202c"
                        borderColor={fieldBorder}
                      />
                    </Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="purple"
                      onClick={() => fillRemaining(allocation.id)}
                    >
                      Preencher restante
                    </Button>
                  </HStack>
                )}

                {mode === 'valores' && (
                  <HStack spacing={2} align="flex-end">
                    <Box w="200px">
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={labelColor}
                        mb={1}
                      >
                        Valor (R$)
                      </Text>
                      <ChakraInput
                        size="sm"
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Ex: 150.00"
                        value={allocation.value ?? ''}
                        onChange={onAllocNumber(allocation.id, 'value')}
                        color="#1a202c"
                        borderColor={fieldBorder}
                      />
                    </Box>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="purple"
                      onClick={() => fillRemaining(allocation.id)}
                    >
                      Preencher restante
                    </Button>
                  </HStack>
                )}

                {mode === 'itens' && (
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color={labelColor}
                      mb={2}
                    >
                      Itens deste centro de custo
                    </Text>
                    {items.length === 0 ? (
                      <Text fontSize="xs" color="#94a3b8">
                        Adicione despesas para distribuí-las por item.
                      </Text>
                    ) : (
                      <VStack align="stretch" spacing={1}>
                        {items.map(item => {
                          const owner = itemOwner(item.id);
                          const ownedByOther =
                            owner && owner.id !== allocation.id;
                          return (
                            <Checkbox
                              key={item.id}
                              size="sm"
                              colorScheme="purple"
                              isChecked={(allocation.item_ids || []).includes(
                                item.id,
                              )}
                              isDisabled={ownedByOther}
                              onChange={onToggleItem(allocation.id, item.id)}
                            >
                              <Text fontSize="xs" color="#1a202c">
                                {item.label} — {formatCurrency(item.value)}
                                {ownedByOther && ' (já alocado)'}
                              </Text>
                            </Checkbox>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </VStack>

      <Divider my={4} borderColor="gray.200" />

      {/* Resumo do rateio */}
      <Box
        p={4}
        borderRadius="md"
        bg={exceeds ? '#fef2f2' : '#faf5ff'}
        borderWidth={1}
        borderColor={exceeds ? '#fecaca' : '#e9d5ff'}
      >
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" color={labelColor}>
            Total das despesas
          </Text>
          <Text fontSize="sm" fontWeight="600" color="#1a202c">
            {formatCurrency(total)}
          </Text>
        </HStack>
        <HStack justify="space-between" mb={1}>
          <Text fontSize="sm" color={labelColor}>
            Total rateado
          </Text>
          <Text
            fontSize="sm"
            fontWeight="600"
            color={exceeds ? '#dc2626' : '#6d28d9'}
          >
            {formatCurrency(distributed)}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <Text fontSize="sm" color={labelColor}>
            {remaining >= 0 ? 'Restante a ratear' : 'Excedente'}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="600"
            color={remaining < 0 ? '#dc2626' : '#16a34a'}
          >
            {formatCurrency(Math.abs(remaining))}
          </Text>
        </HStack>

        {exceeds && (
          <HStack spacing={2} mt={3} color="#dc2626">
            <Icon as={RiErrorWarningLine} />
            <Text fontSize="xs" fontWeight="600">
              O total rateado ultrapassa o total das despesas. Ajuste as
              alocações.
            </Text>
          </HStack>
        )}
      </Box>
    </Box>
  );
}
